import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  InputAdornment,
  MenuItem,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import LinkIcon from "@mui/icons-material/Link";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { axiosInstance } from "../../../../utils/axiosInstance";
import { SavedCandidateEndPoint } from "../../../../api-routes";
import CommonSnackbar from "../../../../components/common-components/CommonSnackbar";
import { CandidateDetailsWrapper } from "./style";

const noticePeriodOptions = [
  "Immediate",
  "15 Days",
  "30 Days",
  "60 Days",
  "90 Days",
  "More than 90 Days",
];

const CandidateDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedJobIndex = location.state?.selectedJobIndex ?? 0;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertDetail, setAlertDetail] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const [form, setForm] = useState({
    candidateName: "",
    email: "",
    mobile: "",
    linkedinId: "",
    qualification: "",
    totalExp: "",
    relevantExp: "",
    currentCTC: "",
    expectedCTC: "",
    noticePeriod: "",
    currentLocation: "",
    preferredLocation: "",
    reasonForChange: "",
  });

  useEffect(() => {
    const fetchCandidate = async () => {
      try {
        const res = await axiosInstance.get(
          `${SavedCandidateEndPoint.GET_BY_ID}/${id}`,
        );
        const data = res.data.data;
        setForm({
          candidateName: data.candidateName || "",
          email: data.email || "",
          mobile: data.mobile || "",
          linkedinId: data.linkedinId || "",
          qualification: data.qualification || "",
          totalExp: data.totalExp ?? "",
          relevantExp: data.relevantExp ?? "",
          currentCTC: data.currentCTC ?? "",
          expectedCTC: data.expectedCTC ?? "",
          noticePeriod: data.noticePeriod || "",
          currentLocation: data.currentLocation || "",
          preferredLocation: data.preferredLocation || "",
          reasonForChange: data.reasonForChange || "",
        });
      } catch (err) {
        console.error("Failed to fetch candidate", err);
        setAlertDetail({
          open: true,
          message: "Failed to load candidate details",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchCandidate();
  }, [id]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        totalExp: form.totalExp !== "" ? Number(form.totalExp) : null,
        relevantExp: form.relevantExp !== "" ? Number(form.relevantExp) : null,
        currentCTC: form.currentCTC !== "" ? Number(form.currentCTC) : null,
        expectedCTC: form.expectedCTC !== "" ? Number(form.expectedCTC) : null,
      };
      await axiosInstance.put(
        `${SavedCandidateEndPoint.UPDATE}/${id}`,
        payload,
      );
      setAlertDetail({
        open: true,
        message: "Candidate details saved successfully",
        severity: "success",
      });
      setTimeout(
        () => navigate("/app/selected", { state: { selectedJobIndex } }),
        1000,
      );
    } catch (err) {
      console.error("Failed to save candidate", err);
      setAlertDetail({
        open: true,
        message:
          err.response?.data?.message || "Failed to save candidate details",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Backdrop open sx={{ color: "#fff", zIndex: 1301 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <CandidateDetailsWrapper>
      <CommonSnackbar
        open={alertDetail.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        setOpen={setAlertDetail}
        message={alertDetail.message}
        severity={alertDetail.severity}
      />

      <Box className="page-header">
        <Typography className="page-title">Candidate Details</Typography>
        <Typography className="page-subtitle">
          Enter the comprehensive profile information for the prospective
          candidate.
        </Typography>
      </Box>

      <Box className="form-card">
        <Box className="form-card__body">
          <Typography className="section-title">
            Personal Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">Name</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Full legal name"
                className="text-field"
                value={form.candidateName}
                onChange={handleChange("candidateName")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">Mail</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="email@address.com"
                className="text-field"
                value={form.email}
                onChange={handleChange("email")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">Mobile</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="+1 (555) 000-0000"
                className="text-field"
                value={form.mobile}
                onChange={handleChange("mobile")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">Linkedin ID</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="linkedin.com/in/username"
                className="text-field"
                value={form.linkedinId}
                onChange={handleChange("linkedinId")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Divider className="section-divider" />

          <Typography className="section-title">
            Professional Details
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography className="field-label">Qualification</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Highest degree earned"
                className="text-field"
                value={form.qualification}
                onChange={handleChange("qualification")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography className="field-label">Total Exp (Years)</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 5.5"
                className="text-field"
                type="number"
                value={form.totalExp}
                onChange={handleChange("totalExp")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography className="field-label">
                Relevant Exp (Years)
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="e.g. 3.0"
                className="text-field"
                type="number"
                value={form.relevantExp}
                onChange={handleChange("relevantExp")}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography className="field-label">
                Current CTC (₹ LPA)
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Annual compensation"
                className="text-field"
                type="number"
                value={form.currentCTC}
                onChange={handleChange("currentCTC")}
                onWheel={(e) => e.target.blur()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"></InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography className="field-label">
                Expected CTC (₹ LPA)
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Target compensation"
                className="text-field"
                type="number"
                value={form.expectedCTC}
                onChange={handleChange("expectedCTC")}
                onWheel={(e) => e.target.blur()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"></InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography className="field-label">Notice Period</Typography>
              <TextField
                fullWidth
                size="small"
                select
                className="text-field"
                value={form.noticePeriod}
                onChange={handleChange("noticePeriod")}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (value) =>
                    value || (
                      <span style={{ color: "#919eab" }}>Select duration</span>
                    ),
                }}
              >
                {noticePeriodOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Divider className="section-divider" />

          <Typography className="section-title">
            Location & Expectations
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">Current Location</Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="City, State, Country"
                className="text-field"
                value={form.currentLocation}
                onChange={handleChange("currentLocation")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography className="field-label">
                Preferred Location/Job Location
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Open to relocation?"
                className="text-field"
                value={form.preferredLocation}
                onChange={handleChange("preferredLocation")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FlightTakeoffIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography className="field-label">
                Reason for Job change
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={4}
                placeholder="Briefly detail the candidate's motivation for seeking a new role..."
                className="text-field"
                value={form.reasonForChange}
                onChange={handleChange("reasonForChange")}
              />
            </Grid>
          </Grid>
        </Box>

        <Box className="footer-bar">
          <Button
            variant="outlined"
            className="cancel-btn"
            onClick={() =>
              navigate("/app/selected", { state: { selectedJobIndex } })
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="save-btn"
            startIcon={
              saving ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSave}
            disabled={saving}
          >
            Save Details
          </Button>
        </Box>
      </Box>
    </CandidateDetailsWrapper>
  );
};

export default CandidateDetails;
